import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../context/ChatContext';
import './ChatWidget.css';

function ChatWidget() {
  const { t } = useTranslation();
  const { messages, unreadCount, loading, sending, isOpen, toggleChat, closeChat, send } =
    useChat();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change or chat opens
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim() || sending) return;
    send(input);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = msg.createdAt ? new Date(msg.createdAt).toDateString() : 'unknown';
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {});

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div className="chat-panel">
          <div className="chat-panel-header">
            <div className="chat-panel-header-info">
              <MessageCircle size={20} />
              <span className="chat-panel-title">{t('chat.title')}</span>
            </div>
            <button type="button" className="btn btn-ghost chat-panel-close" onClick={closeChat}>
              <X size={18} />
            </button>
          </div>

          <div className="chat-panel-body">
            {loading && messages.length === 0 ? (
              <div className="chat-panel-loading">
                <Loader size={24} className="chat-spinner" />
                <span>{t('common.loading')}</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-panel-empty">
                <MessageCircle size={40} />
                <p>{t('chat.noMessages')}</p>
                <p className="chat-panel-empty-hint">{t('chat.startConversation')}</p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([dateKey, msgs]) => (
                <div key={dateKey}>
                  <div className="chat-date-divider">
                    <span>{formatDate(msgs[0].createdAt)}</span>
                  </div>
                  {msgs.map((msg) => (
                    <div
                      key={msg.id || msg._id}
                      className={`chat-message ${msg.sender === 'support' ? 'chat-message--support' : 'chat-message--user'}`}
                    >
                      <div className="chat-message-bubble">
                        <p className="chat-message-text">{msg.message || msg.text}</p>
                        <span className="chat-message-time">{formatTime(msg.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-panel-footer">
            <div className="chat-input-wrapper">
              <textarea
                ref={inputRef}
                className="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('chat.placeholder')}
                rows={1}
                disabled={sending}
              />
              <button
                type="button"
                className="chat-send-btn"
                onClick={handleSend}
                disabled={!input.trim() || sending}
                aria-label={t('chat.send')}
              >
                {sending ? <Loader size={18} className="chat-spinner" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button type="button" className="chat-fab" onClick={toggleChat} aria-label={t('chat.title')}>
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {!isOpen && unreadCount > 0 && (
          <span className="chat-fab-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
    </>
  );
}

export default ChatWidget;
